import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumb from '../components/Breadcrumb';
import { 
  Award, Building2, Users, Star, TrendingUp, 
  Trophy, Medal, Target, BarChart3, Crown 
} from 'lucide-react';

const AccreditationDashboard = () => {
  const { user, roles } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orgStats, setOrgStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userOrgId, setUserOrgId] = useState(null);

  useEffect(() => {
    // Kullanıcının organizasyonunu bul
    const orgRole = roles.find(r => r.organizationId);
    if (orgRole) {
      setUserOrgId(orgRole.organizationId);
    }
    loadData();
  }, [roles]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Liderlik tablosu
      const leaderboardRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/certifications/leaderboard`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (leaderboardRes.ok) {
        const data = await leaderboardRes.json();
        setLeaderboard(data);
      }

      // Kullanıcının organizasyon istatistikleri
      const orgRole = roles.find(r => r.organizationId);
      if (orgRole?.organizationId) {
        const statsRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/certifications/organization/${orgRole.organizationId}/stats`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (statsRes.ok) {
          const data = await statsRes.json();
          setOrgStats(data);
        }
      }
    } catch (error) {
      console.error('Veri yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAccreditationBadge = (level) => {
    switch (level?.level) {
      case 'Altın':
        return { icon: '🏆', color: 'from-yellow-400 to-yellow-600', stars: 5 };
      case 'Gümüş':
        return { icon: '🥈', color: 'from-gray-300 to-gray-500', stars: 4 };
      case 'Bronz':
        return { icon: '🥉', color: 'from-orange-300 to-orange-500', stars: 3 };
      case 'Başlangıç':
        return { icon: '⭐', color: 'from-blue-300 to-blue-500', stars: 2 };
      default:
        return { icon: '📋', color: 'from-gray-200 to-gray-400', stars: 0 };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const badge = getAccreditationBadge(orgStats?.accreditationLevel);

  return (
    <div>
      <Breadcrumb items={[{ label: 'Akreditasyon Panosu' }]} />
      
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">🏛️ Kurum Akreditasyon Panosu</h1>
        <p className="text-gray-600">Sertifikalı öğretmen sayısına göre kurum akreditasyon puanları</p>
      </div>

      {/* Kurum Akreditasyon Kartı */}
      {orgStats && (
        <div className={`bg-gradient-to-r ${badge.color} rounded-xl p-6 mb-6 text-white`}>
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-5xl">
              {badge.icon}
            </div>
            <div className="flex-1">
              <p className="text-white/80 text-sm mb-1">Kurumunuzun Akreditasyon Seviyesi</p>
              <h2 className="text-3xl font-bold">
                {orgStats.accreditationLevel?.level || 'Henüz Yok'} Akreditasyon
              </h2>
              <div className="flex gap-1 mt-2">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    size={20} 
                    className={i < badge.stars ? 'fill-current' : 'opacity-30'} 
                  />
                ))}
              </div>
            </div>
            <div className="text-right">
              <p className="text-5xl font-bold">{orgStats.accreditationPoints}</p>
              <p className="text-white/80 text-sm">Akreditasyon Puanı</p>
            </div>
          </div>
        </div>
      )}

      {/* İstatistik Kartları */}
      {orgStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard
            icon={<Users className="text-blue-600" />}
            label="Toplam Öğretmen"
            value={orgStats.totalTeachers}
          />
          <StatCard
            icon={<Award className="text-green-600" />}
            label="Sertifikalı"
            value={orgStats.totalCertified}
            subtext={`%${orgStats.certificationRate}`}
          />
          <StatCard
            icon={<Medal className="text-yellow-600" />}
            label="Pro Seviye"
            value={orgStats.certificationCounts?.Pro || 0}
            points="+50 puan"
          />
          <StatCard
            icon={<Medal className="text-blue-600" />}
            label="Medium Seviye"
            value={orgStats.certificationCounts?.Medium || 0}
            points="+25 puan"
          />
          <StatCard
            icon={<Medal className="text-green-600" />}
            label="Basic Seviye"
            value={orgStats.certificationCounts?.Basic || 0}
            points="+10 puan"
          />
        </div>
      )}

      {/* Puan Tablosu */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Puan Hesaplama */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Target className="text-purple-600" />
            Puan Hesaplama
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🏆</span>
                <span className="font-medium">Pro Öğretmen</span>
              </div>
              <span className="font-bold text-yellow-600">+50 puan</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🥈</span>
                <span className="font-medium">Medium Öğretmen</span>
              </div>
              <span className="font-bold text-blue-600">+25 puan</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🥉</span>
                <span className="font-medium">Basic Öğretmen</span>
              </div>
              <span className="font-bold text-green-600">+10 puan</span>
            </div>
          </div>
        </div>

        {/* Akreditasyon Seviyeleri */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Crown className="text-yellow-500" />
            Akreditasyon Seviyeleri
          </h3>
          <div className="space-y-3">
            {[
              { level: 'Altın', min: 500, icon: '🏆', color: 'yellow' },
              { level: 'Gümüş', min: 300, icon: '🥈', color: 'gray' },
              { level: 'Bronz', min: 150, icon: '🥉', color: 'orange' },
              { level: 'Başlangıç', min: 50, icon: '⭐', color: 'blue' },
            ].map(item => (
              <div 
                key={item.level}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  orgStats?.accreditationLevel?.level === item.level 
                    ? `bg-${item.color}-100 border-2 border-${item.color}-300` 
                    : 'bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{item.icon}</span>
                  <span className="font-medium">{item.level}</span>
                </div>
                <span className="text-gray-600">{item.min}+ puan</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Liderlik Tablosu */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <BarChart3 className="text-blue-600" />
          Kurum Sıralaması
        </h3>
        
        {leaderboard.length === 0 ? (
          <p className="text-center text-gray-500 py-8">Henüz veri yok</p>
        ) : (
          <div className="space-y-2">
            {leaderboard.map((org, index) => {
              const isUserOrg = org.organizationId === userOrgId;
              const orgBadge = getAccreditationBadge(org.accreditationLevel);
              
              return (
                <div 
                  key={org.organizationId}
                  className={`flex items-center justify-between p-4 rounded-lg ${
                    isUserOrg ? 'bg-blue-50 border-2 border-blue-300' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-300 text-gray-700' :
                      index === 2 ? 'bg-orange-300 text-orange-800' :
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {org.organizationName}
                        {isUserOrg && <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">Sizin Kurumunuz</span>}
                      </p>
                      <p className="text-sm text-gray-500">
                        {org.totalCertified} sertifikalı öğretmen
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">{org.accreditationPoints}</p>
                      <p className="text-xs text-gray-500">puan</p>
                    </div>
                    <div className="text-3xl">{orgBadge.icon}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// İstatistik Kartı
const StatCard = ({ icon, label, value, subtext, points }) => (
  <div className="bg-white rounded-xl shadow-sm p-4">
    <div className="flex items-start justify-between">
      <div className="p-2 bg-gray-100 rounded-lg">
        {icon}
      </div>
      {points && <span className="text-xs text-gray-400">{points}</span>}
    </div>
    <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
    <p className="text-sm text-gray-500">{label}</p>
    {subtext && <p className="text-xs text-green-600">{subtext}</p>}
  </div>
);

export default AccreditationDashboard;
